/**
 * SiteDesignManifest → CSS custom properties (brand-stage · site render)
 */

import { copyToneTypographyVars } from '@shared/site/design/design-manifest-mappers';
import { isTenantAuthorableTokenKey } from '@shared/site/design/tenant-authorable-token-keys';
import type { CopyToneTier, SiteDesignManifest } from '@shared/site/types/site-design-manifest';

/**
 * Resolve boundary — defense in depth against the W2 write boundary. Legacy/malformed
 * manifests (pre-existing persisted data, direct DB edits) must never resolve forbidden
 * operational keys (e.g. `--error-color`) into rendered cssVars, even if the API write
 * boundary is bypassed.
 */
export function generateCssVarsFromManifest(manifest: SiteDesignManifest): Record<string, string> {
  const vars: Record<string, string> = {};

  for (const [key, value] of Object.entries(manifest.tokens.primitive)) {
    if (isTenantAuthorableTokenKey(key)) {
      vars[key] = value;
    }
  }
  for (const [key, value] of Object.entries(manifest.tokens.semantic)) {
    if (isTenantAuthorableTokenKey(key)) {
      vars[key] = value;
    }
  }
  if (manifest.tokens.component) {
    for (const [key, value] of Object.entries(manifest.tokens.component)) {
      if (isTenantAuthorableTokenKey(key)) {
        vars[key] = value;
      }
    }
  }

  Object.assign(vars, generateMotionVars(manifest.tokens.motion));
  Object.assign(vars, generateSpacingVars(manifest.tokens.spacing));
  Object.assign(vars, generateCopyToneVars(manifest.experienceGuide.copyTone));
  Object.assign(vars, generateImageOverlayVars(manifest.imageOverlay));

  vars['--site-vibe-id'] = manifest.vibeId;
  return vars;
}

export function generateMotionVars(
  motion: SiteDesignManifest['tokens']['motion']
): Record<string, string> {
  return {
    '--ease-standard': motion.easing,
    '--duration-scale': String(motion.durationScale),
    '--duration-reveal': `calc(320ms * ${motion.durationScale || 1})`,
    '--duration-hover': `calc(180ms * ${motion.durationScale || 1})`,
  };
}

export function generateSpacingVars(
  spacing: SiteDesignManifest['tokens']['spacing']
): Record<string, string> {
  const scale = spacing.densityScale;
  return {
    '--site-density-scale': String(scale),
    '--section-gap-sm': `calc(0.5rem * ${scale})`,
    '--section-gap-md': `calc(1rem * ${scale})`,
    '--section-gap-lg': `calc(2rem * ${scale})`,
    '--section-stack-gap': 'var(--section-gap-lg)',
  };
}

export function generateCopyToneVars(copyTone: CopyToneTier): Record<string, string> {
  return copyToneTypographyVars(copyTone);
}

export function generateImageOverlayVars(
  overlay: SiteDesignManifest['imageOverlay']
): Record<string, string> {
  return {
    '--overlay-hero-opacity': String(overlay.hero.opacity),
    '--overlay-hero-blend': overlay.hero.blendMode ?? 'multiply',
    '--overlay-context-opacity': String(overlay.context.opacity),
    '--overlay-texture-opacity': String(overlay.texture.opacity),
    '--overlay-texture-blur': `${overlay.texture.blur ?? 0}px`,
  };
}

/** Map semantic tokens to legacy theme.colors for SiteShell */
export function themeColorsFromDesignCssVars(
  cssVars: Record<string, string>,
  primitive: Record<string, string>
): Record<string, string> {
  return {
    primary: primitive['--color-brand-primary'] ?? cssVars['--color-brand-primary'] ?? '#1e293b',
    secondary:
      primitive['--color-brand-secondary'] ?? cssVars['--color-brand-secondary'] ?? '#3b82f6',
    surface: primitive['--color-brand-surface'] ?? cssVars['--color-brand-surface'] ?? '#f8fafc',
    onSurface:
      primitive['--color-brand-on-surface'] ?? cssVars['--color-brand-on-surface'] ?? '#0f172a',
    accent: primitive['--color-brand-accent'] ?? cssVars['--color-brand-accent'] ?? '#3b82f6',
  };
}

export function themeFontsFromManifest(manifest: SiteDesignManifest): Record<string, string> {
  return {
    heading: manifest.tokens.primitive['--font-display'] ?? 'system-ui, sans-serif',
    body: manifest.tokens.primitive['--font-body'] ?? 'system-ui, sans-serif',
  };
}
